import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import articlePlaceholder from '../../../../assets/articlePlaceholder.png';
import { TopSellingGarmentItem } from '../../../../hooks';
import { CurrencyFormatterOptions } from '../../../../hooks/useCurrencyTools';
import { Typography } from '../../../../stories/atoms';
import { ContainerDrawer } from '../../../../stories/molecules/container-drawer/ContainerDrawer';
import { soldProductsTranslations } from '../../../../translations';
import { getArticleAssetUrl } from '../../../../utils/soldProducts';
import {
  StyledAboutInfo,
  StyledDetailsCard,
  StyledDetailsProperty,
  StyledDrawerContainer,
  StyledProductImage,
} from './ProductSlider.styles';

type ProductSliderProps = {
  isOpen: boolean;
  getFormattedCurrency: (value: number, options?: CurrencyFormatterOptions) => string;
  productDetails?: TopSellingGarmentItem | null;
  onClose?: () => void;
  currencyCode?: string;
};

export const ProductSlider: FC<React.PropsWithChildren<ProductSliderProps>> = ({
  productDetails,
  getFormattedCurrency,
  isOpen,
  onClose,
  currencyCode,
  children,
}) => {
  const { t } = useTranslation();

  const getTopSellingProperties = (item: TopSellingGarmentItem) => {
    return [
      {
        label: t(soldProductsTranslations.piecesSold.key, soldProductsTranslations.piecesSold.defaultValue),
        value: item?.soldPieces ?? '-',
      },
      {
        label: t(soldProductsTranslations.salesNet.key, soldProductsTranslations.salesNet.defaultValue),
        value: getFormattedCurrency(item?.moneyGenerated),
      },
    ];
  };

  const productProperties = !productDetails ? [] : getTopSellingProperties(productDetails);
  const imageUrl = productDetails
    ? getArticleAssetUrl({
        articleId: `${productDetails?.productId}${productDetails?.articleIdentifier}`,
        assetType: 'descriptiveStillLife',
        rendition: 'medium',
      })
    : undefined;

  return (
    <ContainerDrawer
      isOpen={isOpen}
      onClose={onClose}
      sliderContent={
        <StyledDrawerContainer>
          <Typography type="button" margin={[0, 0, 3, 0]}>
            {t(
              soldProductsTranslations.productDetailsView.title.key,
              soldProductsTranslations.productDetailsView.title.defaultValue,
            )}
          </Typography>
          <div data-testid="product-details-view">
            <StyledProductImage
              data-testid="product-image"
              src={imageUrl}
              alt=""
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = articlePlaceholder;
              }}
            />
            <StyledAboutInfo data-testid="product-info">
              <div>
                <Typography type="body3" margin={[0, 0, 2, 0]}>
                  {productDetails?.title}
                </Typography>
                <Typography type="body3">{`${productDetails?.whitePrice} ${currencyCode}`} </Typography>
              </div>

              <Typography textAlign="left" color="textGray" type="legal">
                {t(soldProductsTranslations.productId.key, soldProductsTranslations.productId.defaultValue)}:{' '}
                {productDetails?.productId}
              </Typography>
            </StyledAboutInfo>

            <StyledDetailsCard data-testid="product-details">
              {productProperties.map((property, index) => (
                <StyledDetailsProperty key={index}>
                  <Typography>{property.label}</Typography>
                  <Typography>{property.value}</Typography>
                </StyledDetailsProperty>
              ))}
            </StyledDetailsCard>
          </div>
        </StyledDrawerContainer>
      }
      width="250px"
    >
      {children}
    </ContainerDrawer>
  );
};
